import { action, computed, observable } from 'mobx'
import type { Id } from '@/infra/id'
import {
  Consultation,
  type ConsultationProps,
} from '@/features/jobs/consultations/Consultation'
import { Model } from '@/infra/model'
import type { SubmittedJob } from './details/SubmittedJob'

/**
 * Known job types.
 */
export type JobType =
  | TaxJobType
  | ConsultationJobType
  | BookkeepingJobType
  | MiscJobType

/**
 * Props for {@link JobType}.
 */
export type JobTypeProps =
  | TaxJobTypeProps
  | ConsultationJobTypeProps
  | BookkeepingJobTypeProps
  | MiscJobTypeProps

/**
 * Props for {@link TaxJobType}.
 */
export interface TaxJobTypeProps {
  type: 'Tax'
  consultation?: ConsultationProps | null
  prepJobId?: Id | null
  reviewJobId?: Id | null
  isExtensionOnlyJob?: boolean
}

/**
 * Represents a job that deals primarily with a tax return.
 */
export class TaxJobType extends Model<TaxJobType> {
  /**
   * If specified, represents the add-on consultation for the tax job.
   */
  @observable accessor consultation: Consultation | null = null

  /**
   * If specified, contains a link to the prep job.
   */
  @observable accessor prepJobId: Id | null = null

  /**
   * If specified, contains a link to the review job.
   */
  @observable accessor reviewJobId: Id | null = null

  /**
   * If true, indicates that the job is an extension only job.
   */
  @observable accessor isExtensionOnlyJob = false

  constructor(public job: SubmittedJob) {
    super()
  }

  /**
   * Whether consultation scheduling is allowed.
   */
  @computed
  get isConsultationSchedulingAllowed(): boolean {
    return Boolean(
      this.job.status !== 'InfoGathering' &&
      this.job.status !== 'Unclaimed' &&
      this.consultation?.isSettingAvailabilityAllowed,
    )
  }

  /** @inheritdoc */
  @action.bound
  override set({
    consultation,
    ...props
  }: Partial<Omit<TaxJobTypeProps, 'type'>>): this {
    if (consultation === null) {
      this.consultation = null
    } else if (consultation) {
      if (!this.consultation || this.consultation.id !== consultation.id) {
        this.consultation = new Consultation().set(consultation)
      } else {
        this.consultation.set(consultation)
      }
    }

    return super.set(props)
  }
}

/**
 * Props for {@link ConsultationJobType}.
 */
export interface ConsultationJobTypeProps {
  type: 'Consultation'
  consultation?: ConsultationProps | null
}

/**
 * Represents a consultation-only job.
 */
export class ConsultationJobType extends Model<ConsultationJobType> {
  @observable accessor consultation: Consultation | null = null

  constructor(public job: SubmittedJob) {
    super()
  }

  /**
   * Whether consultation scheduling is allowed.
   */
  @computed
  get isConsultationSchedulingAllowed(): boolean {
    return Boolean(this.consultation?.isSettingAvailabilityAllowed)
  }

  /** @inheritdoc */
  @action.bound
  override set({ consultation }: Partial<ConsultationJobTypeProps>): this {
    if (consultation === null) {
      this.consultation = null
    } else if (consultation) {
      if (!this.consultation || this.consultation.id !== consultation.id) {
        this.consultation = new Consultation().set(consultation)
      } else {
        this.consultation.set(consultation)
      }
    }
    return this
  }
}

/**
 * Props for {@link BookkeepingJobType}.
 */
export interface BookkeepingJobTypeProps {
  type: 'Bookkeeping'
}

/**
 * Represents a job that deals primarily with bookkeeping.
 */
export class BookkeepingJobType extends Model<BookkeepingJobType> {
  /** @inheritdoc */
  @action.bound
  override set(props: Partial<Omit<BookkeepingJobTypeProps, 'type'>>): this {
    return super.set(props)
  }
}

/**
 * Props for {@link MiscJobType}.
 */
export interface MiscJobTypeProps {
  type: 'Misc'
}

/**
 * Represents a miscellaneous job.
 */
export class MiscJobType extends Model<MiscJobType> {
  /** @inheritdoc */
  @action.bound
  override set(props: Partial<Omit<MiscJobTypeProps, 'type'>>): this {
    return super.set(props)
  }
}

/**
 * Match arms used for {@link matchJobType}.
 */
interface MatchJobTypeArmsCore<R> {
  tax: (type: TaxJobType) => R
  bookkeeping: (type: BookkeepingJobType) => R
  consultation: (type: ConsultationJobType) => R
  misc: (type: MiscJobType) => R
}

/**
 * Match arms used for {@link matchJobType}.
 */
export type MatchJobTypeArms<R> =
  | MatchJobTypeArmsCore<R>
  | (Partial<MatchJobTypeArmsCore<R>> & { default: () => R })

/**
 * Matches on the job type.
 *
 * @param type
 * @param arms
 */
export function matchJobType<T>(type: JobType, arms: MatchJobTypeArms<T>): T {
  if (type instanceof TaxJobType && arms.tax) {
    return arms.tax(type)
  }

  if (type instanceof ConsultationJobType && arms.consultation) {
    return arms.consultation(type)
  }

  if (type instanceof BookkeepingJobType && arms.bookkeeping) {
    return arms.bookkeeping(type)
  }

  if (type instanceof MiscJobType && arms.misc) {
    return arms.misc(type)
  }

  if ('default' in arms) {
    return arms.default()
  }

  // Should be unreachable.
  throw new Error('Unhandled job type')
}
